import { Navigate, Route, Routes } from "react-router-dom";
import { usePanel } from "../panelContext";
import { FAMILY_SECTIONS } from "../sections";
import { FamilyScope } from "./FamilyScope";
import { PersonScope } from "./PersonScope";

/**
 * The panel's two scopes, as routes (ADR 0021 decision 2).
 *
 * They mirror the API exactly — `/families/:familyId/…` for a family, `/me/…` for the person —
 * because a URL that disagreed with the route it calls is a translation layer with nothing to
 * translate.
 *
 * **The root picks a family and redirects**, rather than being a screen of its own. A member
 * of one family (the common case, and all MCP allows) should never see a "choose your family"
 * step, and a member of several lands in the first of theirs and switches from the header.
 * `replace` so the Back button leaves the app instead of bouncing between the root and the
 * family it chose.
 */
/**
 * Where the root sends a member: **straight to a section**, not to the family root.
 *
 * `FamilyScope` redirects its own index too, and both are needed — a typed or bookmarked
 * `/families/<id>` has to work. What this avoids is doing it in two hops on the common path,
 * where each hop renders a `<Navigate>` and therefore renders *nothing*: the panel flashed
 * blank between the loading card and the shell, which is a frame of "the page is empty" on a
 * surface people open when something is already wrong.
 */
function landing(familyId: string): string {
  return `/families/${familyId}/${FAMILY_SECTIONS[0]?.path ?? ""}`;
}

export function Scopes() {
  const { families } = usePanel();
  // Non-empty by construction: `Panel` renders this only when a family is open to them.
  const first = families[0];

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={first === undefined ? "/me" : landing(first.familyId)} replace />}
      />
      <Route path="/families/:familyId/*" element={<FamilyScope />} />
      <Route path="/me/*" element={<PersonScope />} />
      {/* Anything else is a stale link or a typed URL. The root knows how to answer it. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
